/**
 * extractSelectOutline — token-aware clause walker for the basic SELECT renderer.
 * Identifies top-level (paren-depth 0) clause keyword positions inside a
 * statement range and slices the buffer text into clause body ranges.
 * Pure function; uses `tokenSkip` for string / comment / quoted-identifier
 * skipping so it's structural (VQE-RO9 — no regex).
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

export type SelectKeyword =
  | 'SELECT' | 'DISTINCT' | 'ALL' | 'TOP'
  | 'FROM' | 'WHERE' | 'GROUP_BY' | 'HAVING' | 'ORDER_BY' | 'WINDOW'
  | 'LIMIT' | 'OFFSET' | 'FETCH' | 'WITH';

export interface KeywordSpan {
  readonly keyword: SelectKeyword;
  readonly start: number;
  readonly end: number;
  // For TOP, `valueEnd` marks the end of the TOP value (after PERCENT / WITH
  // TIES). Other keywords leave this undefined.
  readonly valueEnd?: number;
}

export interface ClauseSpan {
  readonly start: number;
  readonly end: number;
}

export interface SelectOutline {
  readonly statementStart: number;
  readonly statementEnd: number;
  readonly hasWith: boolean;
  readonly distinct: boolean;
  readonly all: boolean;
  readonly selectList: ClauseSpan | null;
  readonly from: ClauseSpan | null;
  readonly where: ClauseSpan | null;
  readonly groupBy: ClauseSpan | null;
  readonly having: ClauseSpan | null;
  readonly orderBy: ClauseSpan | null;
  readonly window: ClauseSpan | null;
  readonly top: ClauseSpan | null;
  readonly limit: ClauseSpan | null;
  readonly offset: ClauseSpan | null;
  readonly fetch: ClauseSpan | null;
  readonly keywords: readonly KeywordSpan[];
}

const TERMINATING_KEYWORDS: ReadonlySet<SelectKeyword> = new Set<SelectKeyword>([
  'FROM', 'WHERE', 'GROUP_BY', 'HAVING', 'ORDER_BY', 'WINDOW', 'LIMIT', 'OFFSET', 'FETCH',
]);

interface ScanState {
  readonly source: string;
  readonly endExclusive: number;
  readonly options: ParseOptions;
  index: number;
  paren: number;
}

function readWord(state: ScanState): { word: string; start: number; end: number } | null {
  const c = state.source[state.index];
  if (!IDENT_START.test(c)) return null;
  const start = state.index;
  state.index++;
  while (state.index < state.endExclusive && IDENT_CONT.test(state.source[state.index])) {
    state.index++;
  }
  return { word: state.source.slice(start, state.index), start, end: state.index };
}

function peekWordAt(source: string, from: number, endExclusive: number): string | null {
  let i = from;
  while (i < endExclusive && /\s/.test(source[i])) i++;
  if (i >= endExclusive || !IDENT_START.test(source[i])) return null;
  const start = i;
  i++;
  while (i < endExclusive && IDENT_CONT.test(source[i])) i++;
  return source.slice(start, i).toUpperCase();
}

function classifyWord(state: ScanState, raw: string, end: number): SelectKeyword | null {
  const up = raw.toUpperCase();
  if (state.paren !== 0) return null;
  switch (up) {
    case 'SELECT': return 'SELECT';
    case 'DISTINCT': return 'DISTINCT';
    case 'ALL': return 'ALL';
    case 'TOP': return 'TOP';
    case 'FROM': return 'FROM';
    case 'WHERE': return 'WHERE';
    case 'HAVING': return 'HAVING';
    case 'LIMIT': return 'LIMIT';
    case 'OFFSET': return 'OFFSET';
    case 'FETCH': return 'FETCH';
    case 'WITH': return 'WITH';
    case 'WINDOW': return 'WINDOW';
    case 'GROUP': return peekWordAt(state.source, end, state.endExclusive) === 'BY' ? 'GROUP_BY' : null;
    case 'ORDER': return peekWordAt(state.source, end, state.endExclusive) === 'BY' ? 'ORDER_BY' : null;
    default: return null;
  }
}

function consumeSecondWord(state: ScanState): void {
  // Used to advance past the trailing 'BY' for GROUP/ORDER.
  while (state.index < state.endExclusive && /\s/.test(state.source[state.index])) state.index++;
  if (state.index < state.endExclusive && IDENT_START.test(state.source[state.index])) {
    state.index++;
    while (state.index < state.endExclusive && IDENT_CONT.test(state.source[state.index])) {
      state.index++;
    }
  }
}

// Advance past TOP's value (paren group, single token, or numeric literal +
// optional PERCENT / WITH TIES). Returns the new end-of-value index.
function consumeTopValue(state: ScanState): number {
  // Skip leading whitespace.
  while (state.index < state.endExclusive && /\s/.test(state.source[state.index])) state.index++;
  if (state.index >= state.endExclusive) return state.index;
  const c = state.source[state.index];
  if (c === '(') {
    // Match the paren group.
    let depth = 0;
    while (state.index < state.endExclusive) {
      const ch = state.source[state.index];
      if (ch === '(') depth++;
      else if (ch === ')') { depth--; state.index++; if (depth === 0) break; continue; }
      state.index++;
    }
  } else {
    // Numeric or single identifier token.
    while (
      state.index < state.endExclusive &&
      !/\s/.test(state.source[state.index]) &&
      state.source[state.index] !== ',' &&
      state.source[state.index] !== '('
    ) {
      state.index++;
    }
  }
  // Optionally consume PERCENT.
  const afterValue = state.index;
  let probe = afterValue;
  while (probe < state.endExclusive && /\s/.test(state.source[probe])) probe++;
  const peek = peekWordAt(state.source, probe, state.endExclusive);
  if (peek === 'PERCENT') {
    state.index = probe;
    while (state.index < state.endExclusive && IDENT_CONT.test(state.source[state.index])) {
      state.index++;
    }
  }
  // Optionally consume WITH TIES.
  probe = state.index;
  while (probe < state.endExclusive && /\s/.test(state.source[probe])) probe++;
  if (peekWordAt(state.source, probe, state.endExclusive) === 'WITH') {
    let after = probe;
    while (after < state.endExclusive && IDENT_CONT.test(state.source[after])) after++;
    let probe2 = after;
    while (probe2 < state.endExclusive && /\s/.test(state.source[probe2])) probe2++;
    if (peekWordAt(state.source, probe2, state.endExclusive) === 'TIES') {
      state.index = probe2;
      while (state.index < state.endExclusive && IDENT_CONT.test(state.source[state.index])) {
        state.index++;
      }
    }
  }
  return state.index;
}

export function findSelectKeywords(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): KeywordSpan[] {
  const ctx = skipContextFor(options);
  const out: KeywordSpan[] = [];
  const state: ScanState = { source, endExclusive, options, index: start, paren: 0 };

  while (state.index < endExclusive) {
    const skipped = trySkipNonCode(source, state.index, endExclusive, ctx);
    if (skipped >= 0) { state.index = skipped; continue; }

    const c = source[state.index];
    if (c === '(') { state.paren++; state.index++; continue; }
    if (c === ')') { if (state.paren > 0) state.paren--; state.index++; continue; }
    if (/\s/.test(c)) { state.index++; continue; }

    if (IDENT_START.test(c)) {
      const before = state.index;
      const w = readWord(state);
      if (w) {
        const kw = classifyWord(state, w.word, w.end);
        if (kw === 'GROUP_BY' || kw === 'ORDER_BY') {
          consumeSecondWord(state);
          out.push({ keyword: kw, start: w.start, end: state.index });
        } else if (kw === 'TOP') {
          const valueEnd = consumeTopValue(state);
          out.push({ keyword: kw, start: w.start, end: w.end, valueEnd });
        } else if (kw) {
          out.push({ keyword: kw, start: w.start, end: w.end });
        }
        if (state.index === before) state.index++;
      } else {
        state.index++;
      }
      continue;
    }

    state.index++;
  }
  return out;
}

function spanFromTo(start: number, end: number): ClauseSpan | null {
  if (end <= start) return null;
  return { start, end };
}

function nextTerminatorIndex(keywords: readonly KeywordSpan[], from: number): number {
  for (let i = from; i < keywords.length; i++) {
    if (TERMINATING_KEYWORDS.has(keywords[i].keyword)) return i;
  }
  return -1;
}

export function buildSelectOutlineFromKeywords(
  statementStart: number,
  statementEnd: number,
  keywords: readonly KeywordSpan[],
): SelectOutline {
  const hasWith = keywords.length > 0 && keywords[0].keyword === 'WITH';
  const selectIndex = keywords.findIndex((k) => k.keyword === 'SELECT');
  if (selectIndex < 0) {
    return {
      statementStart, statementEnd, hasWith, distinct: false, all: false,
      selectList: null, from: null, where: null, groupBy: null, having: null,
      orderBy: null, window: null, top: null, limit: null, offset: null, fetch: null, keywords,
    };
  }

  let cursor = keywords[selectIndex].end;
  let distinct = false;
  let all = false;
  let topSpan: ClauseSpan | null = null;
  let next = selectIndex + 1;
  while (next < keywords.length) {
    const k = keywords[next];
    if (k.keyword === 'DISTINCT') { distinct = true; cursor = k.end; next++; continue; }
    if (k.keyword === 'ALL') { all = true; cursor = k.end; next++; continue; }
    if (k.keyword === 'TOP') {
      const valueEnd = k.valueEnd ?? k.end;
      topSpan = spanFromTo(k.end, valueEnd);
      cursor = valueEnd;
      next++;
      continue;
    }
    break;
  }

  const termIndex = nextTerminatorIndex(keywords, next);
  const selectListEnd = termIndex >= 0 ? keywords[termIndex].start : statementEnd;
  const selectList = spanFromTo(cursor, selectListEnd);

  const find = (k: SelectKeyword): KeywordSpan | undefined => keywords.slice(next).find((x) => x.keyword === k);
  const fromKw = find('FROM');
  const whereKw = find('WHERE');
  const groupByKw = find('GROUP_BY');
  const havingKw = find('HAVING');
  const orderByKw = find('ORDER_BY');
  const windowKw = find('WINDOW');
  const limitKw = find('LIMIT');
  const offsetKw = find('OFFSET');
  const fetchKw = find('FETCH');

  function clauseEnd(kw: KeywordSpan | undefined): number {
    if (!kw) return statementEnd;
    const idx = keywords.indexOf(kw);
    for (let i = idx + 1; i < keywords.length; i++) {
      if (TERMINATING_KEYWORDS.has(keywords[i].keyword)) return keywords[i].start;
    }
    return statementEnd;
  }

  return {
    statementStart, statementEnd, hasWith, distinct, all,
    selectList,
    top: topSpan,
    from: fromKw ? spanFromTo(fromKw.end, clauseEnd(fromKw)) : null,
    where: whereKw ? spanFromTo(whereKw.end, clauseEnd(whereKw)) : null,
    groupBy: groupByKw ? spanFromTo(groupByKw.end, clauseEnd(groupByKw)) : null,
    having: havingKw ? spanFromTo(havingKw.end, clauseEnd(havingKw)) : null,
    orderBy: orderByKw ? spanFromTo(orderByKw.end, clauseEnd(orderByKw)) : null,
    window: windowKw ? spanFromTo(windowKw.end, clauseEnd(windowKw)) : null,
    limit: limitKw ? spanFromTo(limitKw.end, clauseEnd(limitKw)) : null,
    offset: offsetKw ? spanFromTo(offsetKw.end, clauseEnd(offsetKw)) : null,
    fetch: fetchKw ? spanFromTo(fetchKw.end, clauseEnd(fetchKw)) : null,
    keywords,
  };
}

export function extractSelectOutline(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): SelectOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const keywords = findSelectKeywords(source, start, end, options);
  return buildSelectOutlineFromKeywords(start, end, keywords);
}
